import { useCallback, useRef } from 'react';
import RestoApiService from '../utils/RestoApiService';
import { MapLocation } from '../utils/SuperclusterManager';

interface UseRestaurantLoaderProps {
  map: google.maps.Map | null;
  restoApiService: RestoApiService;
  locations: MapLocation[];
  selectedMarkerId: string | null;
  setLocations: React.Dispatch<React.SetStateAction<MapLocation[]>>;
  setSelectedMarkerId: React.Dispatch<React.SetStateAction<string | null>>;
}

export function useRestaurantLoader({
  map,
  restoApiService,
  locations,
  selectedMarkerId,
  setLocations,
  setSelectedMarkerId,
}: UseRestaurantLoaderProps) {
  const initialCenterSet = useRef(false);

  const loadRestaurantById = useCallback(
    async (restoId: string, setCenterOnMap: boolean = true): Promise<MapLocation | null> => {
      if (!map) {
        console.error("Map is not initialized");
        return null;
      }

      // First check if we already have this location in our array
      const existingLocation = locations.find(loc => loc.id === restoId);
      if (existingLocation) {
        setSelectedMarkerId(restoId);
        
        if (setCenterOnMap && existingLocation.lat && existingLocation.lng) {
          const position = { lat: existingLocation.lat, lng: existingLocation.lng };
          map.setCenter(position);
          map.setZoom(18);
        }
        
        return existingLocation;
      }

      try {
        const result = await restoApiService.fetchRestoById(restoId);
        
        if (result.success && result.data) {
          const entry = result.data;
          
          // Validate that we have valid coordinates
          const latitude = entry.latitude;
          const longitude = entry.longitude;
          
          if (latitude && longitude) {
            const newLocation: MapLocation = {
              id: restoId,
              lat: latitude,
              lng: longitude,
              entry: entry,
            };
            
            // Add the new location to the array
            setLocations(prev => [...prev, newLocation]);
            
            // Set it as selected
            setSelectedMarkerId(restoId);
            
            if (setCenterOnMap) {
              const position = { lat: latitude, lng: longitude };
              map.setCenter(position);
              map.setZoom(18); // Zoom in to clearly show the marker
            }
            initialCenterSet.current = true; // Mark initial center as set
            
            return newLocation; // Return the newly created location
          } else {
            console.warn(`Restaurant with ID ${restoId} found but lacks coordinates.`);
            throw new Error(`Restaurant with ID ${restoId} lacks coordinates`);
          }
        } else {
          console.error(`Failed to fetch restaurant with ID ${restoId}: ${result.error}`);
          throw new Error(`Failed to fetch restaurant with ID ${restoId}: ${result.error}`);
        }
      } catch (error) {
        console.error("DEBUG INFINITE LOOP CHECK: Unhandled exception in loadRestaurantById:", error);
        throw error; // Re-throw to allow proper handling by caller
      }
    },
    [map, restoApiService, locations, selectedMarkerId, setLocations, setSelectedMarkerId],
  );

  return { loadRestaurantById, initialCenterSet };
}